import { Link, useLocation } from 'react-router-dom';
import { ShoppingBag, User, Menu, X, LayoutDashboard, LogOut, Wallet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { useCart } from '@/contexts/CartContext';
import { useState, useEffect } from 'react';
import { windowsAPI, settingsAPI } from '@/lib/api';
import { CategoryMenu } from '@/components/CategoryMenu';
import { money } from '@/lib/format';

export const Navbar = () => {
  const { user, isAdmin, logout } = useAuth();
  const { itemCount } = useCart();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [windowStatus, setWindowStatus] = useState(null);
  const [windowBarVisible, setWindowBarVisible] = useState(true);
  const location = useLocation();

  const isActive = (path) => location.pathname === path;

  useEffect(() => {
    windowsAPI.getActive().then(r => setWindowStatus(r.data)).catch(() => {});
    settingsAPI.getPublic().then(r => setWindowBarVisible(r.data?.window_bar_visible !== false)).catch(() => {});
  }, []);

  const windowOpen = windowStatus?.active;
  const activeVendors = windowStatus?.vendors || [];

  return (
    <>
      {/* Announcement / Order Window Bar */}
      {windowBarVisible && (
      <div
        data-testid="announcement-bar"
        className={`w-full py-2.5 text-center font-body text-sm tracking-wide ${
          windowStatus && !windowOpen
            ? 'bg-red-700 text-white'
            : 'bg-charcoal text-white'
        }`}
      >
        {windowStatus ? (
          windowOpen ? (
            <span>
              {activeVendors.length} vendor{activeVendors.length !== 1 ? 's' : ''} currently accepting orders
              {activeVendors.length <= 3 && activeVendors.length > 0 && (
                <> — <strong>{activeVendors.map(v => v.name).join(', ')}</strong></>
              )}
            </span>
          ) : (
            <span>No vendors currently accepting orders. Check back soon for the next ordering period.</span>
          )
        ) : (
          <span>Welcome to AVZ Wholesale — Premium wholesale products from trusted suppliers</span>
        )}
      </div>
      )}

      {/* Main Navigation */}
      <header
        data-testid="main-navbar"
        className="sticky top-0 z-50 bg-background border-b border-border"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-24 md:h-[7.5rem] overflow-visible relative">
            {/* Left: Logo / Brand */}
            <Link to="/" className="flex items-center translate-y-2" data-testid="logo-link">
              <img src="/logo.png" alt="AVZ Wholesale" className="h-[7.5rem] md:h-[10.5rem] w-auto logo-colored" />
            </Link>

            {/* Center: Nav Links (Desktop) - absolutely centered */}
            <nav className="hidden md:flex items-center gap-10 absolute left-1/2 -translate-x-1/2" data-testid="desktop-nav">
              <Link
                to="/"
                data-testid="nav-home"
                className={`font-body text-sm uppercase tracking-[0.15em] transition-all duration-200 pb-1 ${
                  isActive('/')
                    ? 'text-foreground font-semibold border-b-2 border-foreground'
                    : 'text-foreground/50 hover:text-foreground'
                }`}
              >
                Home
              </Link>
              <Link
                to="/shop"
                data-testid="nav-shop"
                className={`font-body text-sm uppercase tracking-[0.15em] transition-all duration-200 pb-1 ${
                  isActive('/shop')
                    ? 'text-foreground font-semibold border-b-2 border-foreground'
                    : 'text-foreground/50 hover:text-foreground'
                }`}
              >
                Shop
              </Link>
              <Link
                to="/about"
                data-testid="nav-about"
                className={`font-body text-sm uppercase tracking-[0.15em] transition-all duration-200 pb-1 ${
                  isActive('/about')
                    ? 'text-foreground font-semibold border-b-2 border-foreground'
                    : 'text-foreground/50 hover:text-foreground'
                }`}
              >
                About
              </Link>
            </nav>

            {/* Right: Icons */}
            <div className="flex items-center gap-2 md:gap-3">
              {/* Admin Link */}
              {isAdmin && (
                <Link to="/admin" className="flex flex-col items-center" data-testid="admin-link">
                  <Button variant="ghost" size="icon" className="text-foreground/70 hover:text-foreground hover:bg-transparent rounded-none h-10 w-10 [&_svg]:!h-5 [&_svg]:!w-5" aria-label="Admin dashboard">
                    <LayoutDashboard strokeWidth={2} />
                  </Button>
                  <span className="hidden md:block font-body text-[10px] uppercase tracking-[0.15em] text-foreground/60 -mt-1.5">Admin</span>
                </Link>
              )}

              {/* Cart — customers only (admins manage via dashboard) */}
              {!isAdmin && (
                <Link to="/cart" className="relative flex flex-col items-center" data-testid="cart-link">
                  <Button variant="ghost" size="icon" className="text-foreground/70 hover:text-foreground hover:bg-transparent rounded-none h-10 w-10 [&_svg]:!h-5 [&_svg]:!w-5" aria-label="My Cart">
                    <ShoppingBag strokeWidth={2} />
                  </Button>
                  <span className="hidden md:block font-body text-[10px] uppercase tracking-[0.15em] text-foreground/60 -mt-1.5">My Cart</span>
                  {itemCount > 0 && (
                    <span
                      data-testid="cart-count"
                      className="absolute top-0 right-0 bg-gold text-charcoal text-[10px] w-5 h-5 rounded-full flex items-center justify-center font-bold shadow-md"
                    >
                      {itemCount}
                    </span>
                  )}
                </Link>
              )}

              {/* User Actions */}
              {user ? (
                <div className="hidden md:flex items-center gap-2 md:gap-3">
                  {!isAdmin && Number(user.credit_balance || 0) > 0 && (
                    <Link
                      to="/profile"
                      data-testid="navbar-credit-pill"
                      title="Your account credit"
                      className="flex items-center gap-1.5 px-3 h-8 rounded-full bg-gold/15 border border-gold/30 text-gold hover:bg-gold/25 transition-colors"
                    >
                      <Wallet className="h-3.5 w-3.5" strokeWidth={2.2} />
                      <span className="font-body text-xs font-bold">R{money(Number(user.credit_balance))}</span>
                    </Link>
                  )}
                  {!isAdmin && (
                    <Link to="/profile" className="flex flex-col items-center" data-testid="profile-link">
                      <Button variant="ghost" size="icon" className="text-foreground/70 hover:text-foreground hover:bg-transparent rounded-none h-10 w-10 [&_svg]:!h-5 [&_svg]:!w-5" aria-label="My Orders">
                        <User strokeWidth={2} />
                      </Button>
                      <span className="font-body text-[10px] uppercase tracking-[0.15em] text-foreground/60 -mt-1.5">My Orders</span>
                    </Link>
                  )}
                  <button
                    type="button"
                    onClick={logout}
                    data-testid="logout-btn"
                    aria-label="Logout"
                    className="flex flex-col items-center group focus:outline-none"
                  >
                    <span className="inline-flex items-center justify-center text-foreground/70 group-hover:text-foreground rounded-none h-10 w-10 transition-colors">
                      <LogOut className="h-5 w-5" strokeWidth={2} />
                    </span>
                    <span className="font-body text-[10px] uppercase tracking-[0.15em] text-foreground/60 group-hover:text-foreground/80 -mt-1.5 transition-colors">
                      Logout
                    </span>
                  </button>
                </div>
              ) : (
                <Link to="/auth" className="hidden md:flex flex-col items-center" data-testid="login-link">
                  <Button variant="ghost" size="icon" className="text-foreground/70 hover:text-foreground hover:bg-transparent rounded-none h-10 w-10 [&_svg]:!h-5 [&_svg]:!w-5" aria-label="Sign in">
                    <User strokeWidth={2} />
                  </Button>
                  <span className="font-body text-[10px] uppercase tracking-[0.15em] text-foreground/60 -mt-1.5">Sign In</span>
                </Link>
              )}

              {/* Mobile Menu Toggle */}
              <Button
                variant="ghost"
                size="icon"
                className="md:hidden text-foreground hover:bg-transparent rounded-none h-10 w-10 [&_svg]:!h-5 [&_svg]:!w-5"
                onClick={() => setMobileOpen(!mobileOpen)}
                data-testid="mobile-menu-toggle"
                aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              >
                {mobileOpen ? <X strokeWidth={2} /> : <Menu strokeWidth={2} />}
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="md:hidden bg-background border-t border-border px-6 pb-8" data-testid="mobile-menu">
            <nav className="flex flex-col gap-0 pt-4">
              <Link
                to="/"
                onClick={() => setMobileOpen(false)}
                className={`font-body text-sm uppercase tracking-[0.15em] py-4 border-b border-gray-50 transition-colors ${
                  isActive('/')
                    ? 'text-foreground font-semibold'
                    : 'text-foreground/50 hover:text-foreground'
                }`}
              >
                Home
              </Link>
              <Link
                to="/shop"
                onClick={() => setMobileOpen(false)}
                className={`font-body text-sm uppercase tracking-[0.15em] py-4 border-b border-gray-50 transition-colors ${
                  isActive('/shop')
                    ? 'text-foreground font-semibold'
                    : 'text-foreground/50 hover:text-foreground'
                }`}
              >
                Shop
              </Link>
              <Link
                to="/about"
                onClick={() => setMobileOpen(false)}
                data-testid="mobile-nav-about"
                className={`font-body text-sm uppercase tracking-[0.15em] py-4 border-b border-gray-50 transition-colors ${
                  isActive('/about')
                    ? 'text-foreground font-semibold'
                    : 'text-foreground/50 hover:text-foreground'
                }`}
              >
                About
              </Link>
              {user ? (
                <>
                  {!isAdmin && (
                    <Link
                      to="/profile"
                      onClick={() => setMobileOpen(false)}
                      className="font-body text-sm uppercase tracking-[0.15em] py-4 border-b border-border text-foreground/50 hover:text-foreground"
                    >
                      My Account
                    </Link>
                  )}
                  {isAdmin && (
                    <Link
                      to="/admin"
                      onClick={() => setMobileOpen(false)}
                      className="font-body text-sm uppercase tracking-[0.15em] py-4 border-b border-border text-foreground/50 hover:text-foreground"
                    >
                      Admin Dashboard
                    </Link>
                  )}
                  <button
                    onClick={() => { logout(); setMobileOpen(false); }}
                    className="font-body text-sm uppercase tracking-[0.15em] py-4 text-left text-foreground/30 hover:text-foreground"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <Link
                  to="/auth"
                  onClick={() => setMobileOpen(false)}
                  className="font-body text-sm uppercase tracking-[0.15em] py-4 text-foreground font-semibold"
                >
                  Sign In
                </Link>
              )}
            </nav>
          </div>
        )}
      </header>
      <CategoryMenu />
    </>
  );
};
